import {
  ZslAssetTypeSchema
} from "./chunk-G3CGZSJJ.mjs";
import {
  ZapSchema
} from "./chunk-TDLPIO7B.mjs";
import {
  FieldTypeEnum,
  ModifiedTableSchema
} from "./chunk-LSYVJY5R.mjs";
import {
  RestrictedAgentEditSchema
} from "./chunk-EQSAE4CQ.mjs";
import {
  TemplateChatbotSchema
} from "./chunk-S32O2WNK.mjs";
import {
  ModifiedTemplateInterfaceSchema,
  SupportedBlockTypesEnum
} from "./chunk-CZGEKGY2.mjs";

// edit/index.ts
import { z } from "zod";
var PositionSchema = z.object({
  x: z.number().nullish(),
  y: z.number().nullish()
});
var ServiceSchema = z.object({
  currentImplementation: z.object({
    id: z.string().nullish()
  }).nullish()
});
var NodeDataSchema = z.object({
  label: z.union([
    z.string(),
    z.object({
      type: z.string().nullish(),
      content: z.array(z.any()).nullish()
    })
  ]).nullish(),
  tableId: z.string().nullish(),
  zapId: z.string().nullish(),
  isExpanded: z.boolean().nullish(),
  app: z.string().nullish(),
  services: z.array(ServiceSchema).nullish(),
  parentId: z.string().nullish()
});
var NodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  position: PositionSchema.nullish(),
  data: NodeDataSchema.nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
  dragging: z.boolean().nullish(),
  deletable: z.boolean().nullish(),
  connectable: z.boolean().nullish(),
  selected: z.boolean().nullish(),
  style: z.record(z.any()).nullish()
});
var EdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  className: z.string().nullish(),
  deletable: z.boolean().nullish(),
  markerEnd: z.object({
    type: z.string().nullish(),
    strokeWidth: z.number().nullish(),
    color: z.string().nullish()
  }).nullish()
});
var CanvasSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  account_id: z.number().nullish(),
  customuser_id: z.number().nullish(),
  created: z.string().nullish(),
  lastchanged: z.string().nullish(),
  nodes: z.array(NodeSchema).nullish(),
  edges: z.array(EdgeSchema).nullish(),
  is_public: z.boolean().nullish(),
  access_level: z.string().nullish()
});
var RequestSchema = z.object({
  zapsession: z.string(),
  prompt: z.string().optional(),
  canvas: CanvasSchema.optional(),
  sourceNodeId: z.string().optional()
});
var ZslSchema = z.object({
  zaps: z.array(ZapSchema).optional().describe(
    `Zaps are automation workflows that connect different apps and services together.
    - Each zap consists of a trigger (what starts the automation) and one or more actions (what happens after the trigger)
    - Zaps can connect internal Zapier components (Tables, Interfaces) or external apps
    - When using Zapier Tables, explicitly mention "Zapier Table" in the prompt
    - Zaps can include filters, delays, and multiple actions in sequence
    - Consider edge cases and error handling in your automation design`
  ),
  interfaces: z.array(ModifiedTemplateInterfaceSchema).optional().describe(
    `Interfaces are user-facing pages and forms that allow interaction with the system.
    - Design intuitive user experiences with appropriate form fields and layouts
    - Use form blocks for data collection, table blocks for data display
    - Kanban blocks can visualize workflow states
    - Link blocks are ideal for navigation and resource pages
    - Media blocks enhance visual appeal and context
    - Consider user roles and data access permissions
    - Ensure proper field type mapping when connecting to tables`
  ),
  tables: z.array(ModifiedTableSchema).optional().describe(
    `Tables are the data storage layer of the system.
    - Design tables to store structured data efficiently
    - Choose appropriate field types for each data point
    - Consider relationships between tables
    - Plan for data validation and constraints
    - Design for scalability and performance
    - Consider data access patterns and indexing needs`
  ),
  chatbots: z.array(TemplateChatbotSchema).optional().describe(
    `Chatbots provide conversational interfaces for user interaction.
    - Design natural conversation flows
    - Consider different user intents and responses
    - Integrate with system data and functionality
    - Handle edge cases and fallback responses
    - Consider multi-turn conversations
    - Design for specific use cases and contexts`
  ),
  agents: z.array(RestrictedAgentEditSchema).optional().describe(
    `Agents are AI-powered assistants that can perform tasks autonomously.
    - Only the agent name and workflow instructions can be edited
    - All other properties (id, description, enabled status) are read-only
    - Agents can interact with other system components
    - Design clear and specific workflow instructions
    - Consider the agent's role in the overall system workflow`
  )
});
var ZslSchemaForApi = ZslSchema.extend({
  name: z.string()
});
var InterfaceOutlineBlockFieldSchema = z.object({
  label: z.string(),
  config: z.object({
    inputType: FieldTypeEnum
  })
});
var InterfaceOutlineBlockSchema = z.object({
  type: SupportedBlockTypesEnum.describe(
    "The type of block to create, blocks that require data sources that are not present in the outline should not be used."
  ),
  children: z.array(InterfaceOutlineBlockFieldSchema).nullable().describe(
    "This will only exist for form blocks and should map to the relevant table fields"
  )
});
var InterfaceZsolSchema = z.object({
  id: z.string().describe(
    "In the format _INTERFACE_ID, where ID starts at 1 is unique to each interface. /^_INTERFACE_d+$/"
  ),
  prompt: z.string().describe(
    "A sentence expressing the interface, along with the page (or pages) that needs to be built and what it should do"
  ),
  pages: z.array(
    z.object({
      id: z.string(),
      name: z.string().describe("Name must be under 32 characters").transform((val) => val.slice(0, 32)),
      description: z.string(),
      blocks: z.array(InterfaceOutlineBlockSchema).nullable().describe(
        "A list of the blocks that will be used in the page. If the block is a kanban-block, table-listing-block, or form-block - you can only include that one block per page. You can not include a kanban-block, table-listing-block, or form-block if there is no table to connect to."
      ),
      assetConnectionNotes: z.union([z.string(), z.null()]).describe(
        "Description for how the page is connected to tables and zaps in the proposed system. If the page is not connected to any tables or zaps in the proposed system, set this to null."
      ),
      connectedAssets: z.union([z.array(z.string()), z.null()]).describe(
        "The IDs of the assets that will be connected to this interface page right now. If the page is not connected to any assets right now, set this to null."
      )
    })
  ).nullable()
});
var TableZsolSchema = z.object({
  prompt: z.string().describe(
    "A sentence expressing the table (or tables) and what they should store."
  ),
  tables: z.array(
    z.object({
      id: z.string().describe(
        "In the format _TABLE_ID, where ID starts at 1. /^_TABLE_d+$/"
      ),
      name: z.string(),
      purpose: z.string(),
      fields: z.array(
        z.object({
          name: z.string(),
          id: z.string().describe(
            "In the format _FIELD_N , where N starts at 1 and goes up per field on the table"
          ),
          type: FieldTypeEnum
        })
      ).describe("The names of the fields in the table"),
      connectedAssets: z.array(z.string()).describe(
        "The IDs of the interfaces or zaps that are connected to the table"
      )
    })
  ).nullable()
});
var ChatbotZsolSchema = z.object({
  prompt: z.string().describe("A sentence expressing the chatbot and what it should do."),
  chatbots: z.array(
    z.object({
      id: z.string().describe(
        "In the format _CHATBOT_ID, where ID starts at 1. /^_CHATBOT_d+$/"
      ),
      name: z.string(),
      purpose: z.string(),
      connectedAssets: z.array(z.string()).describe(
        "The IDs of any interfaces, tables, or zaps that are connected to the chatbot"
      )
    })
  ).nullable()
});
var ZapZsolSchema = z.object({
  zapId: z.string().describe(
    "In the format _ZAP_ID, where ID starts at 1 is unique to each zap. /^_ZAP_d+$/"
  ),
  prompt: z.string().describe(
    "A sentence expressing the trigger and subsequent list of actions. If the zap uses a Zapier Table for a trigger or action, make sure to include the words 'Zapier Table'. "
  ),
  apps: z.array(z.string()),
  assetConnectionNotes: z.string().describe(
    "A text description to explain the templated values through params and other components in the system (use their ids explicitly)."
  ),
  connectedAssets: z.array(z.string()).describe("The IDs of the assets that are connected to this zap prompt")
});
var zsolSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().describe("Description must be under 32 characters").transform((val) => val.slice(0, 32)),
  parameters: z.array(
    z.object({
      key: z.string(),
      default: z.string()
    })
  ).default([]),
  chatbots: z.union([z.array(ChatbotZsolSchema), z.null()]),
  interfaces: z.union([z.array(InterfaceZsolSchema), z.null()]),
  tables: z.union([z.array(TableZsolSchema), z.null()]),
  zaps: z.union([z.array(ZapZsolSchema), z.null()])
});
var CanvasAdditionsSchema = z.object({
  id: z.string().describe("The id of the canvas addition"),
  proposal: z.object({
    source_node_id: z.string().optional().describe("The id of the node that this proposal is based on, if any"),
    chatbots: z.array(
      z.object({
        title: z.string().describe("The title of the asset"),
        downstream_prompt: z.string().describe("The prompt for the downstream asset")
      })
    ).optional(),
    interfaces: z.array(
      z.object({
        title: z.string().describe("The title of the asset"),
        downstream_prompt: z.string().describe("The prompt for the downstream asset")
      })
    ).optional(),
    tables: z.array(
      z.object({
        title: z.string().describe("The title of the asset"),
        downstream_prompt: z.string().describe("The prompt for the downstream asset")
      })
    ).optional(),
    zaps: z.array(
      z.object({
        title: z.string().describe("The title of the asset"),
        downstream_prompt: z.string().describe("The prompt for the downstream asset")
      })
    ).optional()
  }).describe("The proposal for the asset"),
  outline: zsolSchema.describe("The outline for the asset").optional(),
  zsl: ZslSchema.optional()
});
var CanvasAdditionsSkeletonSchema = CanvasAdditionsSchema.omit({
  zsl: true
});
var EditCanvasResponseSchema = z.object({
  additions: z.array(CanvasAdditionsSchema),
  removals: z.array(z.object({ id: z.string() })),
  changes: z.array(z.object({ id: z.string() }))
});
var CreateAssetSchema = RequestSchema.required().partial({
  sourceNodeId: true,
  canvas: true
}).extend({
  assetType: ZslAssetTypeSchema
});

export {
  PositionSchema,
  ServiceSchema,
  NodeDataSchema,
  NodeSchema,
  EdgeSchema,
  CanvasSchema,
  RequestSchema,
  ZslSchema,
  ZslSchemaForApi,
  InterfaceOutlineBlockFieldSchema,
  InterfaceOutlineBlockSchema,
  InterfaceZsolSchema,
  TableZsolSchema,
  ChatbotZsolSchema,
  ZapZsolSchema,
  zsolSchema,
  CanvasAdditionsSchema,
  CanvasAdditionsSkeletonSchema,
  EditCanvasResponseSchema,
  CreateAssetSchema
};
